import { useRef, useEffect, useState, FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import auth from "../../fire";
import { useAuthState } from "react-firebase-hooks/auth";
import { signInWithEmailAndPassword } from "firebase/auth";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { ClipLoader } from "react-spinners";

const Login = () => {
  const navigate = useNavigate();
  const [user, loading, error] = useAuthState(auth);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { promiseInProgress } = usePromiseTracker({ delay: 500 });

  useEffect(() => {
    if (loading) {
      // maybe trigger a loading screen
      return;
    }
    if (user) {
      navigate("/");
      return;
    }
  }, [user, loading]);

  const submitHandler = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const email = emailInputRef.current?.value;
    const password = passwordInputRef.current?.value;
    if (!email || !password) return;

    trackPromise(
      signInWithEmailAndPassword(auth, email, password)
        .then(() => {
          setErrorMessage(null);
          navigate("/");
        })
        .catch((error) => {
          console.log("Login error:", error.code);
          // One generic message for any credential failure so the UI can't be
          // used to tell "no such account" from "wrong password" (user
          // enumeration). With Email Enumeration Protection enabled, Firebase
          // returns auth/invalid-credential for both; the granular
          // user-not-found / wrong-password codes are handled by the default too.
          switch (error.code) {
            case "auth/too-many-requests":
              setErrorMessage(
                "Too many login attempts. Please try again later."
              );
              break;
            case "auth/user-disabled":
              setErrorMessage("This account has been disabled.");
              break;
            default:
              setErrorMessage("Incorrect email or password.");
          }
        })
    );
  };

  return (
    <>
      <div className="flex min-h-full items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full">
          <div className="flex flex-col items-center">
            <img
              className="mx-auto w-16 h-auto"
              src="/superset-icon.png"
              alt="Superset"
            />

            <h1 className="mb-1 mt-1 font-bold leading-7 text-indigo-600 text-4xl tracking-tight">
              Superset
            </h1>
            <span className="font-normal text-med tracking-[1.4px] text-indigo-600">
              Workout Tracker
            </span>
            <h2 className="mt-8 text-center text-2xl font-bold tracking-tight text-gray-900">
              Sign in to your account
            </h2>
            {/* <p className="mt-2 text-center text-sm text-gray-600">
              Or{" "}
              <a
                href="#"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                start your 14-day free trial
              </a>
            </p> */}
          </div>
          <form className="mt-8" onSubmit={submitHandler}>
            {/* <input type="hidden" name="remember" defaultValue="true" /> */}
            <div className="-space-y-px rounded-md shadow-sm">
              <div>
                <label htmlFor="email-address" className="sr-only">
                  Email address
                </label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="relative block w-full appearance-none rounded-none rounded-t-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 text-sm"
                  placeholder="Email address"
                  ref={emailInputRef}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="relative block w-full appearance-none rounded-none rounded-b-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 text-sm"
                  placeholder="Password"
                  ref={passwordInputRef}
                />
              </div>
            </div>
            <div className="mt-1 min-h-[1.25rem]">
              {errorMessage && (
                <div className="text-sm text-red-600">{errorMessage}</div>
              )}
            </div>

            {/* <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label
                  htmlFor="remember-me"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <a
                  href="#"
                  className="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  Forgot your password?
                </a>
              </div>
            </div> */}

            <div>
              <button
                type="submit"
                className="mt-4 group relative flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:pointer-events-none"
                disabled={promiseInProgress ? true : false}
              >
                {!promiseInProgress && `Sign in`}
                {promiseInProgress && (
                  <ClipLoader
                    color="rgb(white)"
                    speedMultiplier={0.6}
                    size={"1.25rem"}
                  />
                )}
              </button>
            </div>
            <div className=" px-4 py-4 w-full flex justify-center bg-gray-50">
              <Link
                to="/forgot-password"
                className="text-indigo-600 font-medium text-sm"
              >
                Forgot your password?
              </Link>
            </div>
          </form>
        </div>
        <div className=" px-4 py-4 w-full flex justify-center fixed bottom-0 bg-gray-50">
          <p className="text-sm text-gray-900">
            Don't have an account?{" "}
            <Link to="/sign-up" className="text-indigo-600 font-medium">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </>
  );
};

export default Login;
